import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { Footer } from "@/components/Footer";
import { PostCard } from "@/components/PostCard";
import { api } from "@/lib/api";
import { getSection, resolveSlug } from "@/lib/sections";
import { useSection } from "@/lib/useSections";
import { useAuth } from "@/context/AuthContext";
import { Plus } from "lucide-react";

const Section = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  // Redirect legacy room slugs (mode, table, maison, jardin) to their new home.
  const resolved = resolveSlug(slug);
  const isLegacy = resolved !== slug;
  // All hooks must run on every render (Rules of Hooks). The Navigate below
  // only triggers after all hooks are declared.
  const section = useSection(isLegacy ? resolved : slug);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isLegacy || !section) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`/sections/${slug}`).then(({ data }) => {
      setData(data);
      setLoading(false);
    });
    // Intentionally excluding `section` from deps — useSection() returns a new
    // object reference every render (spread of SECTION_MAP + overrides), which
    // would cause an infinite refetch loop and page flicker. `slug` + `isLegacy`
    // together fully determine when the fetch needs to re-run.
  }, [slug, isLegacy]);

  if (isLegacy) {
    return <Navigate replace to={`/room/${resolved}`} />;
  }

  if (!section) {
    return (
      <div className="app-shell">
        <Navbar />
        <LiveNowBanner />
        <div className="container-belle" style={{ paddingTop: 140 }}>
          <p className="font-serif" style={{ fontStyle: "italic", fontSize: 22 }}>This room doesn't exist.</p>
          <Link to="/rooms" className="link-underline" style={{ color: "var(--brand-accent)" }}>← All rooms</Link>
        </div>
      </div>
    );
  }

  const featured = data?.featured_post;
  const otherPosts = data?.posts?.filter((p) => p.id !== featured?.id) || [];

  return (
    <div data-testid={`section-page-${slug}`} className="app-shell">
      <Navbar />
      <LiveNowBanner />

      {/* HERO MASTHEAD */}
      <div style={{ position: "relative", overflow: "hidden", paddingTop: 72 }}>
        <div style={{ position: "relative", height: 460 }}>
          <img loading="lazy" decoding="async" src={section.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(28,25,23,0.2) 0%, rgba(28,25,23,0.65) 100%)" }} />
          <div className="container-belle" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: 56 }}>
            <div className="overline" style={{ color: "rgba(250,249,245,0.85)" }}>Room · {section.overline}</div>
            <h1 className="editorial-h1" style={{ fontSize: "clamp(56px, 8vw, 112px)", color: "var(--text-inverse)", marginTop: 12, marginBottom: 18 }}>
              {section.label}
            </h1>
            <p className="font-serif" style={{ fontStyle: "italic", fontSize: 22, color: "rgba(250,249,245,0.9)", maxWidth: 580, margin: 0, lineHeight: 1.45 }}>
              {section.description}
            </p>
            {user && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12, marginTop: 28 }}>
                <Link
                  to={`/compose?room=${slug}`}
                  data-testid={`section-post-here-${slug}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "14px 28px",
                    background: section.accent,
                    color: "#FAF9F5",
                    fontSize: 12,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 600,
                    textDecoration: "none",
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,0.32)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.25)"; }}
                >
                  <Plus size={14} strokeWidth={2.2} /> Post in {section.label}
                </Link>
                <Link
                  to="/rooms"
                  data-testid={`section-change-room-${slug}`}
                  style={{
                    fontSize: 12,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: "rgba(250,249,245,0.85)",
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 500,
                    textDecoration: "none",
                    borderBottom: "1px solid rgba(250,249,245,0.45)",
                    paddingBottom: 2,
                    transition: "color 0.18s ease, border-color 0.18s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#FAF9F5"; e.currentTarget.style.borderColor = "#FAF9F5"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(250,249,245,0.85)"; e.currentTarget.style.borderColor = "rgba(250,249,245,0.45)"; }}
                >
                  or post in a different room →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container-belle" style={{ paddingTop: 64, paddingBottom: 32 }}>
        {loading && <p style={{ color: "var(--text-muted)" }}>Loading…</p>}

        {/* FEATURED HERO */}
        {featured && (
          <article style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 56, alignItems: "center", marginBottom: 80 }} className="section-hero">
            <Link to={`/post/${featured.id}`} className="media-zoom" style={{ display: "block", overflow: "hidden", aspectRatio: "4/5" }}>
              <img loading="lazy" decoding="async" src={featured.media_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Link>
            <div>
              <div className="overline" style={{ color: section.accent }}>★ Editor's pick · {section.label}</div>
              {featured.editor_note && (
                <p className="font-serif" style={{ fontSize: 23, fontStyle: "italic", lineHeight: 1.5, color: "var(--text-primary)", borderLeft: `2px solid ${section.accent}`, paddingLeft: 18, margin: "20px 0 28px" }}>
                  {featured.editor_note}
                </p>
              )}
              {featured.post_type === "blog" ? (
                <>
                  <h2 className="editorial-h1" style={{ fontSize: "clamp(30px, 4.5vw, 48px)", marginTop: 0, marginBottom: 18 }}>{featured.title}</h2>
                  <p className="editorial-body" style={{ fontSize: 17 }}>{featured.body?.slice(0, 200)}…</p>
                </>
              ) : (
                <p className="font-serif" style={{ fontSize: 24, fontStyle: "italic", lineHeight: 1.45, margin: 0 }}>
                  "{featured.caption}"
                </p>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
                {featured.author?.avatar_url && <img loading="lazy" decoding="async" src={featured.author.avatar_url} alt="" style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }} />}
                <div>
                  <Link to={`/profile/${featured.author?.username}`} className="font-serif" style={{ fontSize: 16, fontWeight: 500 }}>{featured.author?.display_name}</Link>
                  <div className="overline" style={{ marginTop: 2 }}>{featured.author?.location}</div>
                </div>
              </div>
              <Link to={`/post/${featured.id}`} className="btn-primary" style={{ textDecoration: "none", marginTop: 24, display: "inline-block" }}>
                {featured.post_type === "blog" ? "Read the letter" : "View the post"}
              </Link>
            </div>
          </article>
        )}

        {/* THE REST */}
        {otherPosts.length > 0 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
              <div className="overline">All in {section.label}</div>
              <div style={{ flex: 1, height: 1, background: "var(--border-default)" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }} className="section-grid">
              {otherPosts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          </>
        )}

        {!loading && !featured && otherPosts.length === 0 && (
          <div style={{ padding: "72px 32px", border: "1px solid var(--border-default)", textAlign: "center", background: "var(--bg-alt)" }}>
            <img
              loading="lazy" decoding="async" src="/brand/le-salon-door.png"
              alt=""
              aria-hidden="true"
              style={{ width: 140, height: 140, display: "block", margin: "0 auto 28px" }}
            />
            <p className="font-serif" style={{ fontStyle: "italic", fontSize: 24, lineHeight: 1.4, margin: "0 auto 8px", maxWidth: 480 }}>
              {section.label} is waiting for its first conversation.
            </p>
            <p className="editorial-body" style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 20 }}>
              Knock and enter.
            </p>
            <Link to={`/compose?room=${slug}`} className="btn-accent" style={{ textDecoration: "none", display: "inline-block" }}>
              Open the door →
            </Link>
          </div>
        )}
      </div>

      <Footer />
      <style>{`
        @media (max-width: 900px) {
          .section-hero { grid-template-columns: 1fr !important; gap: 32px !important; }
          .section-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default Section;
